<script setup>
import { ref, computed, nextTick, watch, onUnmounted } from 'vue';
import { useFlowieHandoff } from '@/composables/useFlowieHandoff'

const props = defineProps({
    messages: { type: Array, default: () => [] },
    isGenerating: { type: Boolean, default: false },
    initialMessage: { type: String, default: '' },
});

const emit = defineEmits(['send', 'save-search']);

const messageInput = ref('');

const { flowieComposerVisible, closeFlowieComposer } = useFlowieHandoff(
    () => props.initialMessage,
    messageInput
);
const messagesContainer = ref(null);
const funMessageIndex = ref(0);
const funMessageInterval = ref(null);
const progressWidth = ref(0);
const progressInterval = ref(null);

const funMessages = [
    { icon: 'M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z', text: 'Scanning prospect databases...' },
    { icon: 'M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z', text: 'Searching for ideal matches...' },
    { icon: 'M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21', text: 'Analyzing companies...' },
    { icon: 'M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z', text: 'Finding your perfect leads...' },
    { icon: 'M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z', text: 'Almost there...' },
];

const currentFunMessage = computed(() => funMessages[funMessageIndex.value % funMessages.length]);

const startFunMessages = () => {
    funMessageIndex.value = Math.floor(Math.random() * funMessages.length);
    progressWidth.value = 0;
    funMessageInterval.value = setInterval(() => {
        funMessageIndex.value = (funMessageIndex.value + 1) % funMessages.length;
    }, 3500);
    let elapsed = 0;
    progressInterval.value = setInterval(() => {
        elapsed += 200;
        progressWidth.value = Math.min(90, 30 * Math.log10(elapsed / 500 + 1));
    }, 200);
};

const stopFunMessages = () => {
    if (funMessageInterval.value) { clearInterval(funMessageInterval.value); funMessageInterval.value = null; }
    if (progressInterval.value) { clearInterval(progressInterval.value); progressInterval.value = null; }
    progressWidth.value = 100;
};

watch(() => props.isGenerating, (val) => { if (val) startFunMessages(); else stopFunMessages(); });

const sendMessage = () => {
    const text = messageInput.value.trim();
    if (!text || props.isGenerating) return;
    messageInput.value = '';
    emit('send', text);
};

const handleKeydown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
};

watch(() => props.messages.length, () => {
    nextTick(() => { if (messagesContainer.value) messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight; });
});

onUnmounted(() => { stopFunMessages(); });

const formatNumber = (num) => { if (!num) return '0'; return new Intl.NumberFormat().format(num); };

const renderMarkdown = (text) => {
    if (!text) return '';
    let html = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');
    html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="mf-link">$1</a>');
    html = html.replace(/^(\d+)\.\s+(.+)$/gm, '<li class="ml-4 list-decimal">$2</li>');
    html = html.replace(/^[-•]\s+(.+)$/gm, '<li class="ml-4 list-disc">$1</li>');
    html = html.replace(/\n\n/g, '</p><p class="mt-2">');
    html = html.replace(/\n/g, '<br>');
    return '<p>' + html + '</p>';
};
</script>

<template>
    <div class="flex-1 flex flex-col overflow-hidden">
        <flowie-handoff-composer
            v-model="messageInput"
            :visible="flowieComposerVisible"
            :disabled="isGenerating"
            :placeholder="_mft('lead-hunter:scout.placeholder')"
            @close="closeFlowieComposer"
            @send="sendMessage"
        />

        <div ref="messagesContainer" class="flex-1 overflow-y-auto p-4 space-y-3">
            <div v-if="messages.length === 0 && !isGenerating" class="flex flex-col items-center justify-center px-6 py-12 max-w-md mx-auto">
                <div class="flex items-center justify-center w-14 h-14 mx-auto mb-4 rounded-full bg-aiAgentLeadFinder">
                    <ai-agent-lead-finder-avatar class="w-full h-full" />
                </div>
                <h3 class="text-base font-semibold text-gray-900 mb-1">{{ _mft('lead-hunter:scout.onboarding.title') }}</h3>
                <p class="text-sm text-gray-500 mb-6 text-center">{{ _mft('lead-hunter:scout.onboarding.subtitle') }}</p>

                <div class="w-full space-y-3 mb-6">
                    <div class="flex items-start gap-3">
                        <div class="w-6 h-6 rounded-full bg-aiAgentLeadFinder flex items-center justify-center text-[10px] font-bold text-gray-700 flex-shrink-0 mt-0.5">1</div>
                        <div>
                            <p class="text-xs font-medium text-gray-700 mb-0">{{ _mft('lead-hunter:scout.onboarding.step1Title') }}</p>
                            <p class="text-[11px] text-gray-400 mb-0">{{ _mft('lead-hunter:scout.onboarding.step1Desc') }}</p>
                        </div>
                    </div>
                    <div class="flex items-start gap-3">
                        <div class="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center text-[10px] font-bold text-gray-400 flex-shrink-0 mt-0.5">2</div>
                        <div>
                            <p class="text-xs font-medium text-gray-500 mb-0">{{ _mft('lead-hunter:scout.onboarding.step2Title') }}</p>
                            <p class="text-[11px] text-gray-400 mb-0">{{ _mft('lead-hunter:scout.onboarding.step2Desc') }}</p>
                        </div>
                    </div>
                    <div class="flex items-start gap-3">
                        <div class="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center text-[10px] font-bold text-gray-400 flex-shrink-0 mt-0.5">3</div>
                        <div>
                            <p class="text-xs font-medium text-gray-500 mb-0">{{ _mft('lead-hunter:scout.onboarding.step3Title') }}</p>
                            <p class="text-[11px] text-gray-400 mb-0">{{ _mft('lead-hunter:scout.onboarding.step3Desc') }}</p>
                        </div>
                    </div>
                </div>

                <div class="w-full">
                    <p class="text-[10px] text-gray-400 uppercase tracking-wide mb-2">{{ _mft('lead-hunter:scout.onboarding.tryAsking') }}</p>
                    <div class="flex flex-col gap-2">
                        <button
                            v-for="(example, i) in [
                                _mft('lead-hunter:scout.onboarding.example1'),
                                _mft('lead-hunter:scout.onboarding.example2'),
                                _mft('lead-hunter:scout.onboarding.example3'),
                            ]"
                            :key="i"
                            @click="emit('send', example)"
                            class="text-left px-3 py-2 rounded-lg border border-gray-200 text-xs text-gray-600 hover:bg-aiAgentLeadFinder/10 hover:border-aiAgentLeadFinder transition-colors"
                        >
                            "{{ example }}"
                        </button>
                    </div>
                </div>
            </div>

            <div v-for="(msg, index) in messages" :key="index" :class="msg.type === 'preview' ? '' : (msg.role === 'user' ? 'flex justify-end' : 'flex justify-start')">
                <div v-if="msg.role === 'user'" class="bg-aiAgentLeadFinder text-gray-900 rounded-2xl rounded-br-md px-4 py-2 max-w-[85%] text-sm whitespace-pre-line">
                    {{ msg.content }}
                </div>

                <!-- System preview block -->
                <div
                    v-else-if="msg.type === 'preview'"
                    class="w-full"
                >
                    <div class="bg-white border border-gray-200 rounded-lg p-3 my-1">
                        <!-- With profiles -->
                        <template v-if="msg.profiles && msg.profiles.length > 0">
                            <div class="text-[10px] text-gray-500 font-medium uppercase tracking-wide mb-2">
                                {{ _mft('lead-hunter:scout.preview') }} — {{ msg.profiles.length }} {{ _mft('lead-hunter:scout.of') }} {{ formatNumber(msg.total) }} {{ _mft('lead-hunter:scout.prospectsFound') }}
                            </div>
                            <div class="grid grid-cols-2 gap-2">
                                <scout-contact-card
                                    v-for="(profile, pIdx) in msg.profiles.slice(0, 6)"
                                    :key="pIdx"
                                    :profile="profile"
                                />
                            </div>
                            <div v-if="msg.total > msg.profiles.length" class="text-center text-[10px] text-gray-400 mt-2 pt-2 border-t border-gray-100">
                                +{{ formatNumber(msg.total - msg.profiles.length) }} {{ _mft('lead-hunter:scout.moreProspects') }}
                            </div>
                        </template>
                        <!-- Count only (no profiles from Wiza) -->
                        <template v-else>
                            <div class="flex items-center gap-3 py-2">
                                <div class="flex items-center justify-center w-10 h-10 rounded-lg bg-aiAgentLeadFinder/20">
                                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-aiAgentLeadFinder" viewBox="0 0 20 20" fill="currentColor">
                                        <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z"/>
                                    </svg>
                                </div>
                                <div>
                                    <div class="text-lg font-bold text-gray-900">{{ formatNumber(msg.total) }}</div>
                                    <div class="text-xs text-gray-500">{{ _mft('lead-hunter:scout.prospectsMatchFilters') }}</div>
                                </div>
                            </div>
                        </template>

                        <div v-if="Number(msg.total) > 0" class="mt-3 pt-3 border-t border-gray-100">
                            <button-success
                                @click="emit('save-search', msg.filters_used || null)"
                                size="sm"
                                class="w-full justify-center"
                            >
                                {{ _mft('lead-hunter:scout.saveSearch') }}
                            </button-success>
                        </div>
                    </div>
                </div>

                <div v-else-if="msg.role === 'assistant'" class="max-w-[90%] space-y-2">
                    <div class="bg-gray-100 text-gray-800 rounded-2xl rounded-bl-md px-4 py-2 text-sm" v-html="renderMarkdown(msg.content)"></div>

                    <utility-ai-usage-cta v-if="msg.budgetExceeded" class="ml-1" />

                    <div v-if="msg.search_results" class="ml-1 flex items-center gap-2">
                        <div class="inline-flex items-center gap-1.5 px-3 py-1.5 bg-aiAgentLeadFinder/40 text-gray-800 rounded-full text-xs font-medium">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                                <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z"/>
                            </svg>
                            {{ formatNumber(msg.search_results.total) }} {{ _mft('lead-hunter:scout.prospectsFound') }}
                        </div>
                        <button-success
                            v-if="Number(msg.search_results.total) > 0"
                            @click="emit('save-search', msg.filters_used || null)"
                            size="sm"
                        >
                            {{ _mft('lead-hunter:scout.saveSearch') }}
                        </button-success>
                    </div>

                    <div v-if="msg.preview_profiles && msg.preview_profiles.length > 0" class="ml-1 space-y-1">
                        <p class="text-xs text-gray-500 mb-1"><mf-translate domainkey="lead-hunter:scout.sampleProfiles"></mf-translate></p>
                        <div v-for="(profile, pIdx) in msg.preview_profiles.slice(0, 5)" :key="pIdx" class="flex items-center gap-2 px-3 py-2 bg-white rounded-lg border border-gray-200 text-xs">
                            <div class="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 flex-shrink-0">
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                                    <path fill-rule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clip-rule="evenodd" />
                                </svg>
                            </div>
                            <div class="min-w-0">
                                <p class="font-medium text-gray-900 truncate">{{ profile.full_name }}</p>
                                <p class="text-gray-500 truncate">{{ profile.job_title }} <span v-if="profile.job_company_name">@ {{ profile.job_company_name }}</span></p>
                            </div>
                        </div>
                    </div>

                    <div v-if="msg.filters_used" class="ml-1">
                        <p class="text-xs text-gray-500 mb-1"><mf-translate domainkey="lead-hunter:scout.filtersUsed"></mf-translate></p>
                        <div class="flex flex-wrap gap-1">
                            <template v-for="(values, filterKey) in msg.filters_used" :key="filterKey">
                                <span v-for="(val, vIdx) in (Array.isArray(values) ? values : [values])" :key="`${filterKey}-${vIdx}`" class="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] bg-aiAgentLeadFinder/40 text-gray-800 border border-aiAgentLeadFinder">
                                    <span class="text-gray-500 mr-1">{{ filterKey.replace(/_/g, ' ') }}:</span>
                                    {{ typeof val === 'object' ? (val.v || JSON.stringify(val)) : val }}
                                </span>
                            </template>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="isGenerating" class="flex justify-start generating-appear">
                <div class="w-full max-w-[90%] rounded-2xl rounded-bl-md overflow-hidden shadow-lg border border-aiAgentLeadFinder bg-gradient-to-br from-aiAgentLeadFinder/80 via-white to-aiAgentLeadFinder/30">
                    <div class="h-1 w-full bg-gray-200 overflow-hidden">
                        <div class="h-full bg-aiAgentLeadFinder transition-all duration-700 ease-out" :style="{ width: progressWidth + '%' }"></div>
                    </div>
                    <div class="px-5 py-4">
                        <div class="flex items-center gap-3 mb-3">
                            <div class="generating-icon flex-shrink-0 w-8 h-8 rounded-lg bg-aiAgentLeadFinder flex items-center justify-center" :key="funMessageIndex">
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-gray-800" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                                    <path stroke-linecap="round" stroke-linejoin="round" :d="currentFunMessage.icon" />
                                </svg>
                            </div>
                            <span class="text-sm font-medium text-gray-700 generating-text" :key="'t' + funMessageIndex">{{ currentFunMessage.text }}</span>
                        </div>
                        <div class="space-y-2">
                            <div class="flex gap-2">
                                <div class="h-2 rounded-full bg-aiAgentLeadFinder/60 skeleton-shimmer" style="width: 65%"></div>
                                <div class="h-2 rounded-full bg-aiAgentLeadFinder/40 skeleton-shimmer" style="width: 25%; animation-delay: 0.3s"></div>
                            </div>
                            <div class="flex gap-2">
                                <div class="h-2 rounded-full bg-aiAgentLeadFinder/40 skeleton-shimmer" style="width: 40%; animation-delay: 0.15s"></div>
                                <div class="h-2 rounded-full bg-aiAgentLeadFinder/60 skeleton-shimmer" style="width: 50%; animation-delay: 0.45s"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="border-t border-gray-200 p-3">
            <div class="flex items-end space-x-2">
                <textarea
                    v-model="messageInput"
                    @keydown="handleKeydown"
                    :disabled="isGenerating"
                    :placeholder="_mft('lead-hunter:scout.placeholder')"
                    rows="2"
                    autofocus
                    class="flex-1 resize-none rounded-lg border border-gray-300 px-3 py-2 disabled:opacity-50"
                ></textarea>
                <button-base @click="sendMessage" style-type="primary" :disabled="!messageInput.trim() || isGenerating" size="sm" class="flex-shrink-0 !px-2 !py-2">
                    <template #icon>
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z"/>
                        </svg>
                    </template>
                </button-base>
            </div>
        </div>
    </div>
</template>

<style scoped>
@keyframes skeleton-shimmer { 0%, 100% { opacity: 0.4; } 50% { opacity: 1; } }
.skeleton-shimmer { animation: skeleton-shimmer 2s ease-in-out infinite; }
@keyframes generating-appear { from { opacity: 0; transform: translateY(8px) scale(0.97); } to { opacity: 1; transform: translateY(0) scale(1); } }
.generating-appear { animation: generating-appear 0.4s ease-out; }
@keyframes icon-pop { 0% { transform: scale(0) rotate(-20deg); } 60% { transform: scale(1.15) rotate(5deg); } 100% { transform: scale(1) rotate(0deg); } }
.generating-icon { animation: icon-pop 0.5s cubic-bezier(0.34, 1.56, 0.64, 1); }
@keyframes text-slide { from { opacity: 0; transform: translateX(-8px); } to { opacity: 1; transform: translateX(0); } }
.generating-text { animation: text-slide 0.4s ease-out; }
</style>
